import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose, IoCar, IoSpeedometer, IoColorPalette, IoCheckmarkCircle, IoPricetag } from 'react-icons/io5';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { categories } from '../../data/carData';

const CreateCarModal = ({ 
  isOpen, 
  onClose, 
  onSave,
  isCreating
}) => {
  if (!isOpen) return null;

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.3 }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // Get and validate required fields
    const make = formData.get('make')?.trim();
    const model = formData.get('model')?.trim();
    const year = parseInt(formData.get('year'));
    const category = formData.get('category')?.trim();
    const transmission = formData.get('transmission')?.trim();
    const fuelType = formData.get('fuelType')?.trim();
    const seats = parseInt(formData.get('seats'));
    const doors = parseInt(formData.get('doors'));
    const suitcases = parseInt(formData.get('suitcases'));
    const basePrice = parseFloat(formData.get('basePrice'));
    const totalUnits = parseInt(formData.get('totalUnits') || '1');

    // Validate required fields
    if (!make || !model || isNaN(year) || !category || !transmission || !fuelType || 
        isNaN(seats) || isNaN(doors) || isNaN(suitcases) || isNaN(basePrice)) {
      alert('Please fill in all required fields with valid values');
      return;
    }

    // Generate stringId from make, model and transmission
    const stringId = `${make}_${model}_${transmission}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/(^_|_$)/g, '');

    // Create FormData object with validated fields
    const submitData = new FormData();
    submitData.append('stringId', stringId);
    submitData.append('make', make);
    submitData.append('model', model);
    submitData.append('year', year);
    submitData.append('category', category);
    submitData.append('transmission', transmission);
    submitData.append('fuelType', fuelType);
    submitData.append('seats', seats);
    submitData.append('doors', doors);
    submitData.append('suitcases', suitcases);
    submitData.append('basePrice', basePrice);
    submitData.append('totalUnits', totalUnits);

    // Optional fields
    const status = formData.get('status');
    const description = formData.get('description')?.trim();
    const exteriorColor = formData.get('exteriorColor')?.trim();
    const interiorColor = formData.get('interiorColor')?.trim();
    const drivetrain = formData.get('drivetrain')?.trim();
    const airConditioner = formData.get('airConditioner') === 'true';
    const features = formData.get('features')?.split('\n').filter(f => f.trim()) || [];
    const specifications = {
      engine: formData.get('engine')?.trim(),
      power: formData.get('power')?.trim(),
      consumption: formData.get('consumption')?.trim()
    };

    if (status) submitData.append('status', status);
    if (description) submitData.append('description', description);
    if (exteriorColor) submitData.append('exteriorColor', exteriorColor);
    if (interiorColor) submitData.append('interiorColor', interiorColor);
    if (drivetrain) submitData.append('drivetrain', drivetrain);
    submitData.append('airConditioner', airConditioner);
    submitData.append('features', JSON.stringify(features));
    submitData.append('specifications', JSON.stringify(specifications));

    // Append the image file if one was selected
    const imageFile = formData.get('carImage');
    if (imageFile && imageFile.size > 0) {
      submitData.append('carImage', imageFile);
    }

    onSave(submitData);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm">
        <div className="flex items-center justify-center min-h-screen px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl relative z-10 max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 z-20 bg-white px-6 py-4 border-b flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <IoCar className="text-blue-900 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-900">Add New Car</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <IoClose size={24} className="text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Vehicle Identity Card */}
                <motion.section {...fadeInUp} className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg p-6 text-white shadow-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-blue-100">Make *</label>
                      <input
                        type="text"
                        name="make"
                        required
                        className="mt-1 block w-full rounded-md border-0 bg-blue-800/50 text-white placeholder-blue-300 focus:ring-2 focus:ring-white"
                        placeholder="e.g. BMW"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-100">Model *</label>
                      <input
                        type="text"
                        name="model"
                        required
                        className="mt-1 block w-full rounded-md border-0 bg-blue-800/50 text-white placeholder-blue-300 focus:ring-2 focus:ring-white"
                        placeholder="e.g. X5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-100">Year *</label>
                      <input
                        type="number"
                        name="year"
                        required
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        className="mt-1 block w-full rounded-md border-0 bg-blue-800/50 text-white placeholder-blue-300 focus:ring-2 focus:ring-white"
                        placeholder={new Date().getFullYear()}
                      />
                    </div>
                  </div>
                </motion.section>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Technical Specifications */}
                  <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                    <div className="flex items-center space-x-2 mb-4">
                      <IoSpeedometer className="text-blue-900 text-xl" />
                      <h3 className="text-lg font-medium text-gray-900">Technical Specifications</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Transmission *</label>
                        <select
                          name="transmission"
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select Transmission</option>
                          <option value="manual">Manual</option>
                          <option value="automatic">Automatic</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Fuel Type *</label>
                        <select
                          name="fuelType"
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select Fuel Type</option>
                          <option value="gasoline">Gasoline</option>
                          <option value="diesel">Diesel</option>
                          <option value="electric">Electric</option>
                          <option value="hybrid">Hybrid</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Drivetrain</label>
                        <select
                          name="drivetrain"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select Drivetrain</option>
                          <option value="2WD">2WD</option>
                          <option value="4WD">4WD</option>
                          <option value="AWD">AWD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Air Conditioner</label>
                        <select
                          name="airConditioner"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                    </div>
                  </motion.section>

                  {/* Colors & Appearance */}
                  <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                    <div className="flex items-center space-x-2 mb-4">
                      <IoColorPalette className="text-blue-900 text-xl" />
                      <h3 className="text-lg font-medium text-gray-900">Colors & Appearance</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Exterior Color</label>
                        <input
                          type="text"
                          name="exteriorColor"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="e.g. Alpine White"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Interior Color</label>
                        <input
                          type="text"
                          name="interiorColor"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="e.g. Black Leather"
                        />
                      </div>
                    </div>
                  </motion.section>
                </div>

                {/* Features Grid */}
                <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                    <div className="flex items-center space-x-2 mb-4">
                      <IoCheckmarkCircle className="text-blue-900 text-xl" />
                      <h3 className="text-lg font-medium text-gray-900">Category & Features</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Category *</label>
                        <select
                          name="category"
                          required
                          className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select Category</option>
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Seats *</label>
                        <input
                          type="number"
                          name="seats"
                          required
                          min="1"
                          max="15"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Doors *</label>
                        <input
                          type="number"
                          name="doors"
                          required
                          min="1"
                          max="5"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Suitcase Capacity *</label>
                        <input
                          type="number"
                          name="suitcases"
                          required
                          min="0"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Engine</label>
                        <input
                          type="text"
                          name="engine"
                          placeholder="e.g. 2.0L 4-cylinder"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Power</label>
                        <input
                          type="text"
                          name="power"
                          placeholder="e.g. 150 HP"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Fuel Consumption</label>
                        <input
                          type="text"
                          name="consumption"
                          placeholder="e.g. 7.5L/100km"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Features (one per line)</label>
                        <textarea
                          name="features"
                          rows="4"
                          placeholder="Enter features like:&#10;Fuel Tank 46L&#10;Engine 1.4L&#10;GPS Navigation"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        ></textarea>
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          name="description"
                          rows="3"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        ></textarea>
                      </div>
                    </div>
                  </motion.section>

                {/* Car Image Upload */}
                <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                  <div className="flex items-center space-x-2 mb-4">
                    <IoColorPalette className="text-blue-900 text-xl" />
                    <h3 className="text-lg font-medium text-gray-900">Car Image</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Upload Image *</label>
                      <input
                        type="file"
                        name="carImage"
                        accept="image/*"
                        required
                        className="mt-1 block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                      <p className="mt-1 text-sm text-gray-500">Upload a clear image of the car. Supported formats: JPG, PNG, WEBP</p>
                    </div>
                  </div>
                </motion.section>

                {/* Pricing Card */}
                <motion.section {...fadeInUp} className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg p-6 text-white shadow-lg">
                  <div className="flex items-center space-x-2 mb-4">
                    <IoPricetag className="text-white text-xl" />
                    <h3 className="text-lg font-medium">Pricing & Availability</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-green-100">Base Price (€/day) *</label>
                      <input
                        type="number"
                        name="basePrice"
                        required
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full rounded-md border-0 bg-green-500/50 text-white placeholder-green-200 focus:ring-2 focus:ring-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-100">Total Units *</label>
                      <input
                        type="number"
                        name="totalUnits"
                        required
                        min="0"
                        className="mt-1 block w-full rounded-md border-0 bg-green-500/50 text-white placeholder-green-200 focus:ring-2 focus:ring-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-100">Status</label>
                      <select
                        name="status"
                        className="mt-1 block w-full rounded-md border-0 bg-green-500/50 text-white focus:ring-2 focus:ring-white"
                      >
                        <option value="available">Available</option>
                        <option value="maintenance">Under Maintenance</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                    </div>
                  </div>
                </motion.section>

                {/* Actions */}
                <motion.div {...fadeInUp} className="sticky bottom-0 bg-white pt-4 pb-4 border-t mt-8">
                  <div className="flex justify-end gap-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isCreating}
                      className="px-6 py-2 text-white bg-blue-900 hover:bg-blue-800 rounded-full shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                    >
                      {isCreating ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <IoCar />
                          <span>Create Car</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default CreateCarModal;